import type { FixedRowProgramLayout } from "$src/core/models/epg";
import programTimeRangeMs from "$src/core/utils/epg/program-time-range-ms";

type TimedRow = { start: number; end: number };

/**
 * Layout row index for `instantMs`, given a channel's `programLayouts` — already
 * sorted by start with invalid ranges dropped (see layoutProgramsFixedRows). Reads
 * each row's cached time range in order instead of re-parsing and re-sorting the
 * raw programs on every call.
 *
 * Picks the row that contains instantMs, else the closest row in a gap, else an edge.
 */
const findClosestProgramLayoutIndexToInstant = (
  programLayouts: FixedRowProgramLayout[],
  instantMs: number,
): number => {
  const rows: TimedRow[] = [];
  for (const { entry } of programLayouts) {
    const range = programTimeRangeMs(entry);
    // The fixed-row layout step already dropped invalid ranges, so rows stays
    // index-aligned with programLayouts; the guard is only for type-narrowing.
    if (range) {
      rows.push(range);
    }
  }

  if (!rows.length) {
    return 0;
  }

  for (let i = 0; i < rows.length; i++) {
    if (instantMs >= rows[i].start && instantMs < rows[i].end) {
      return i;
    }
  }

  if (instantMs < rows[0].start) {
    return 0;
  }

  const last = rows[rows.length - 1];
  if (instantMs >= last.end) {
    return rows.length - 1;
  }

  for (let i = 0; i < rows.length - 1; i++) {
    if (instantMs >= rows[i].end && instantMs < rows[i + 1].start) {
      const distToPrevEnd = instantMs - rows[i].end;
      const distToNextStart = rows[i + 1].start - instantMs;
      return distToPrevEnd <= distToNextStart ? i : i + 1;
    }
  }

  return rows.length - 1;
};

export default findClosestProgramLayoutIndexToInstant;
